import { useCallback, useEffect, useRef } from "react";
import type { ScriptDocument } from "@supermanus/document-model";

const MAX_HISTORY = 100;
const COALESCE_MS = 800;

export type HistoryPersistOptions = {
  /** When false, mutation is not added to the undo stack (locks, remote sync, etc.). */
  recordHistory?: boolean;
  /**
   * Consecutive edits with the same key within COALESCE_MS replace the last
   * undo entry instead of pushing a new one (typing in a cell).
   */
  coalesceKey?: string;
};

type HistoryEntry = {
  doc: ScriptDocument;
  coalesceKey?: string;
  at: number;
};

function cloneDoc(doc: ScriptDocument): ScriptDocument {
  return structuredClone(doc);
}

/**
 * Undo/redo stack around document mutations.
 * Call `pushBeforeChange(prev)` before applying a user edit, or use
 * `wrapPersist` to instrument an existing persist function.
 */
export function useDocumentHistory(
  docRef: { current: ScriptDocument | null },
  applyDoc: (doc: ScriptDocument) => void | Promise<void>,
) {
  const undoStack = useRef<HistoryEntry[]>([]);
  const redoStack = useRef<HistoryEntry[]>([]);
  const applyingHistory = useRef(false);

  const pushBeforeChange = useCallback(
    (prev: ScriptDocument, options: HistoryPersistOptions = {}) => {
      if (applyingHistory.current) return;
      if (options.recordHistory === false) return;

      const now = Date.now();
      const top = undoStack.current[undoStack.current.length - 1];
      if (
        options.coalesceKey &&
        top?.coalesceKey === options.coalesceKey &&
        now - top.at < COALESCE_MS
      ) {
        // Keep the original "before" snapshot; only refresh the timestamp
        top.at = now;
        redoStack.current = [];
        return;
      }

      undoStack.current.push({
        doc: cloneDoc(prev),
        coalesceKey: options.coalesceKey,
        at: now,
      });
      if (undoStack.current.length > MAX_HISTORY) {
        undoStack.current.shift();
      }
      redoStack.current = [];
    },
    [],
  );

  const undo = useCallback(() => {
    const current = docRef.current;
    const entry = undoStack.current.pop();
    if (!current || !entry) return false;
    redoStack.current.push({ doc: cloneDoc(current), at: Date.now() });
    applyingHistory.current = true;
    docRef.current = entry.doc;
    void Promise.resolve(applyDoc(entry.doc)).finally(() => {
      applyingHistory.current = false;
    });
    return true;
  }, [applyDoc, docRef]);

  const redo = useCallback(() => {
    const current = docRef.current;
    const entry = redoStack.current.pop();
    if (!current || !entry) return false;
    undoStack.current.push({ doc: cloneDoc(current), at: Date.now() });
    applyingHistory.current = true;
    docRef.current = entry.doc;
    void Promise.resolve(applyDoc(entry.doc)).finally(() => {
      applyingHistory.current = false;
    });
    return true;
  }, [applyDoc, docRef]);

  const clear = useCallback(() => {
    undoStack.current = [];
    redoStack.current = [];
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;

      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select")) {
        // Native undo inside title fields etc.
        return;
      }

      const key = event.key.toLowerCase();
      if (key === "z" && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        event.stopPropagation();
        undo();
        return;
      }
      if ((key === "z" && event.shiftKey) || key === "y") {
        event.preventDefault();
        event.stopPropagation();
        redo();
      }
    }

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [undo, redo]);

  return { pushBeforeChange, undo, redo, clear };
}
